<template>
  <v-dialog :value="value" max-width="480" :persistent="saving" @input="!$event && close()">
    <v-card v-if="attendance">
      <v-card-title class="text-h6 font-weight-bold">Anular atención</v-card-title>
      <v-card-text>
        <div class="info-grid mb-4">
          <div>
            <div class="pw-label">Mascota</div>
            <div class="pw-value">{{ attendance.petName }}</div>
          </div>
          <div>
            <div class="pw-label">Tutor</div>
            <div class="pw-value">{{ attendance.userDisplayName }}</div>
          </div>
          <div>
            <div class="pw-label">Fecha de atención</div>
            <div class="pw-value">{{ formatDate(attendance.attendedAt || attendance.createdAt) }}</div>
          </div>
          <div>
            <div class="pw-label">Monto</div>
            <div class="pw-value">{{ formatClp(attendance.pawerAmount) }}</div>
          </div>
        </div>

        <v-text-field
          v-model="reason"
          label="Motivo de la anulación *"
          placeholder="Ej: atención duplicada, error de registro, no corresponde a la cobertura"
          counter="255"
          maxlength="255"
          outlined
          dense
        ></v-text-field>

        <div class="impact" :class="{ 'impact--nc': needsCreditNote }">{{ impactMessage }}</div>

        <v-alert v-if="errorMessage" type="error" dense outlined class="mt-3 mb-0">{{ errorMessage }}</v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text :disabled="saving" @click="close">Cancelar</v-btn>
        <v-btn color="error" depressed :loading="saving" :disabled="!canConfirm" @click="confirm">
          Confirmar Anulación
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ClinicSettlementsService from '@/services/clinic-settlements.service';
import { formatClp, formatDate, settlementErrorMessage } from '@/utils/clinic-settlements.utils';

const MIN_REASON_LENGTH = 3;

// Confirmación de la anulación de una atención (US3). El mensaje cambia según la
// liquidación ya tenga folio: sin factura el monto baja de inmediato; con factura pasa
// a "Requiere NC" y el monto se mantiene hasta registrar la NC.
// Emite `canceled` con el detalle actualizado y `input(false)` para cerrarse.
export default {
  name: 'CancelAttendanceDialog',
  props: {
    value: { type: Boolean, default: false },
    clinicId: { type: String, default: '' },
    cutoffDate: { type: String, default: '' },
    attendance: { type: Object, default: null },
    settlement: { type: Object, default: null }
  },
  data() {
    return { reason: '', saving: false, errorMessage: '' };
  },
  computed: {
    needsCreditNote() {
      return !!(this.settlement && this.settlement.invoiceNumber);
    },
    canConfirm() {
      return this.reason.trim().length >= MIN_REASON_LENGTH && !this.saving;
    },
    impactMessage() {
      if (!this.settlement || !this.attendance) return '';
      const current = this.settlement.pawerAmount || 0;
      const amount = this.attendance.pawerAmount || 0;
      if (!this.needsCreditNote) {
        return `Al anular esta atención, el monto total a liquidar a la clínica pasará de ${formatClp(
          current
        )} a ${formatClp(current - amount)}.`;
      }
      const creditNote = (this.settlement.creditNoteAmount || 0) + amount;
      return (
        'Esta atención ya fue facturada. Al anular, la factura pasará al estado ‘Requiere NC’ y el Monto Pawer se ' +
        `mantendrá en ${formatClp(current)} hasta que se registre la Nota de Crédito por ${formatClp(creditNote)}; ` +
        `una vez registrada, el monto bajará a ${formatClp(current - creditNote)}.`
      );
    }
  },
  watch: {
    value(open) {
      if (open) {
        this.reason = '';
        this.errorMessage = '';
      }
    }
  },
  methods: {
    formatClp,
    formatDate,
    close() {
      if (this.saving) return;
      this.$emit('input', false);
    },
    async confirm() {
      if (!this.canConfirm) return;
      this.saving = true;
      this.errorMessage = '';
      const res = await ClinicSettlementsService.cancelAttendance(
        this.clinicId,
        this.cutoffDate,
        this.attendance.invoiceId,
        this.reason.trim()
      );
      this.saving = false;
      if (res.status !== 'ok') {
        this.errorMessage = settlementErrorMessage(res, 'No se pudo anular la atención. Intenta nuevamente.');
        return;
      }
      this.$emit('canceled', res.data);
      this.$emit('input', false);
    }
  }
};
</script>

<style scoped>
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 16px;
}
.break-all {
  word-break: break-all;
}
.impact {
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.45;
  background: var(--pw-surface-subtle);
  color: var(--pw-text);
}
.impact--nc {
  background: var(--pw-warning-tint);
}
</style>
